import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSubModule } from '../../contexts/SubModuleContext';

interface ModuleIndicatorProps {
  /** Gjeldende modul-id (samme verdi som ?module=-parameteren i URL). */
  moduleId: string;
}

/**
 * Diskret indikator som viser hvilken modul man er i (modul-id).
 *
 * - Vises på ALLE modulsider (rendres i WarehouseMain som omslutter alt innhold).
 * - Vises KUN for administrator-rollen (operatorGroupId === '3').
 * - Klikk kopierer `?module=<id>` (+ `&sub=<undermodul>` når en undermodul er aktiv)
 *   til utklippstavlen — nyttig for å bygge dyplenker
 *   (f.eks. https://compass.ipb.no/?module=admin-data-inspector&...).
 * - Undermodul leses fra SubModuleContext, satt av moduler med interne undervisninger
 *   (f.eks. administrasjon → System Logger). Generisk for hele systemet.
 *
 * Bevisst lav synlighet (liten, halvgjennomsiktig, nederst til venstre) så den ikke
 * forstyrrer det vanlige grensesnittet.
 */
export const ModuleIndicator: React.FC<ModuleIndicatorProps> = ({ moduleId }) => {
  const { user } = useAuth();
  const { subModuleId } = useSubModule();
  const [copied, setCopied] = useState(false);

  // Kun synlig for administrator (rolle 3)
  if (user?.operatorGroupId !== '3') {
    return null;
  }

  // Dyplenke-parameter: ?module=<id>[&sub=<undermodul>]
  const param = subModuleId
    ? `?module=${moduleId}&sub=${subModuleId}`
    : `?module=${moduleId}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(param);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Utklippstavle utilgjengelig (f.eks. ikke-sikker kontekst) — ignorer stille.
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      title="Klikk for å kopiere ?module=-parameter"
      className="fixed bottom-2 left-2 z-40 select-none rounded-md border border-gray-300/60 bg-gray-900/40 px-2 py-0.5 font-mono text-[10px] leading-none text-white/70 opacity-30 backdrop-blur-sm transition-opacity hover:opacity-100 focus:opacity-100 focus:outline-none"
    >
      {copied
        ? '✓ kopiert'
        : subModuleId
          ? `module=${moduleId} › ${subModuleId}`
          : `module=${moduleId}`}
    </button>
  );
};
